import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useMoveOrderCustomer } from '@/api/hooks'
import type { CustomerSummary, OrderDetail } from '@/api/types'
import { Dialog } from '@/components/AppDialog'
import { CustomerPicker } from '@/components/CustomerPicker'
import { ErrorBanner, formatMoney } from '@/components/common'

/**
 * Puts an order under the customer it should have been taken against.
 *
 * <p>A namesake picked off the list, or a duplicate record made in a hurry.
 * Renaming the customer is not the fix - that renames somebody else's customer
 * on all of their own orders - so the order moves instead.</p>
 *
 * <p>And it does not move alone: the bills, the receipts, the credit notes, the
 * refunds and the instalments all carry their own copy of who they are for, and
 * each of the two customers is shown as owing what their orders come to. They
 * all move together or none of them does.</p>
 */
export function MoveCustomerDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const move = useMoveOrderCustomer()
  const [customer, setCustomer] = useState<CustomerSummary | null>(null)
  const [reason, setReason] = useState('')

  useEffect(() => {
    setCustomer(null)
    setReason('')
    move.reset()
    // Re-read only when a different order is opened; resetting on every render
    // of the same one would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id])

  if (!order) return null

  const somebodyElse = Boolean(customer) && customer!.id !== order.customerId
  const valid = somebodyElse && Boolean(reason.trim())

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move {order.orderNumber} to another customer</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={move.error} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Taken against {order.customerName} · {formatMoney(order.totalAmount)} billed ·{' '}
          {formatMoney(order.balanceAmount)} outstanding
        </Typography>

        <Stack spacing={2}>
          <CustomerPicker
            value={customer}
            onChange={setCustomer}
            label="The customer it belongs to"
            required
            helperText="Search by name, mobile, email or customer code"
          />
          <TextField
            label="Why it is moving"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            fullWidth
            placeholder="Taken against a namesake — confirmed with the customer on the phone"
            helperText="Kept on the order, and it is what the office is asked about later"
          />
        </Stack>

        {/* Said before it happens rather than discovered in a ledger later. */}
        <Alert severity="info" sx={{ mt: 2 }}>
          The bills, receipts, credit notes, refunds and instalments on this order all move with
          it, and what each of the two customers is shown as owing changes by{' '}
          {formatMoney(order.balanceAmount)}. Invoices already issued are rewritten to read as the
          new customer — reissue any that have been sent.
        </Alert>

        {customer && !somebodyElse ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This order is already under {customer.name}.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || move.isPending}
          onClick={() =>
            move.mutate(
              { id: order.id, customerId: customer!.id, reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Move the order
        </Button>
      </DialogActions>
    </Dialog>
  )
}
